/* global WebSocket */ import { AbstractRelay as AbstractRelay } from "./abstract-relay.js";
import { normalizeURL } from "./utils.js";
import { alwaysTrue } from "./helpers.js";
export class AbstractSimplePool {
  relays = new Map();
  seenOn = new Map();
  trackRelays = false;
  verifyEvent;
  enablePing;
  enableReconnect;
  automaticallyAuth;
  trustedRelayURLs = new Set();
  onRelayConnectionFailure;
  onRelayConnectionSuccess;
  allowConnectingToRelay;
  maxWaitForConnection;
  _WebSocket;
  constructor(opts){
    this.verifyEvent = opts.verifyEvent;
    this._WebSocket = opts.websocketImplementation;
    this.enablePing = opts.enablePing;
    this.enableReconnect = opts.enableReconnect || false;
    this.automaticallyAuth = opts.automaticallyAuth;
    this.onRelayConnectionFailure = opts.onRelayConnectionFailure;
    this.onRelayConnectionSuccess = opts.onRelayConnectionSuccess;
    this.allowConnectingToRelay = opts.allowConnectingToRelay;
    this.maxWaitForConnection = opts.maxWaitForConnection || 3000;
  }
  async ensureRelay(url, params) {
    url = normalizeURL(url);
    let relay = this.relays.get(url);
    if (!relay) {
      relay = new AbstractRelay(url, {
        verifyEvent: this.trustedRelayURLs.has(url) ? alwaysTrue : this.verifyEvent,
        websocketImplementation: this._WebSocket,
        enablePing: this.enablePing,
        enableReconnect: this.enableReconnect
      });
      relay.onclose = ()=>{
        this.relays.delete(url);
      };
      this.relays.set(url, relay);
    }
    if (this.automaticallyAuth) {
      const authSignerFn = this.automaticallyAuth(url);
      if (authSignerFn) {
        relay.onauth = authSignerFn;
      }
    }
    try {
      await relay.connect({
        timeout: params?.connectionTimeout,
        abort: params?.abort
      });
    } catch (err) {
      this.relays.delete(url);
      throw err;
    }
    return relay;
  }
  close(relays) {
    relays.map(normalizeURL).forEach((url)=>{
      this.relays.get(url)?.close();
      this.relays.delete(url);
    });
  }
  subscribe(relays, filter, params) {
    const request = [];
    const uniqUrls = [];
    for(let i = 0; i < relays.length; i++){
      const url = normalizeURL(relays[i]);
      if (!request.find((r)=>r.url === url)) {
        if (uniqUrls.indexOf(url) === -1) {
          uniqUrls.push(url);
          request.push({
            url,
            filter: filter
          });
        }
      }
    }
    return this.subscribeMap(request, params);
  }
  subscribeMany(relays, filter, params) {
    return this.subscribe(relays, filter, params);
  }
  subscribeMap(requests, params) {
    const grouped = new Map();
    for (const req of requests){
      const { url, filter } = req;
      if (!grouped.has(url)) grouped.set(url, []);
      grouped.get(url).push(filter);
    }
    const groupedRequests = Array.from(grouped.entries()).map(([url, filters])=>({
        url,
        filters
      }));
    if (this.trackRelays) {
      params.receivedEvent = (relay, id)=>{
        let set = this.seenOn.get(id);
        if (!set) {
          set = new Set();
          this.seenOn.set(id, set);
        }
        set.add(relay);
      };
    }
    const _knownIds = new Set();
    const subs = [];
    // batch all EOSEs into a single
    const eosesReceived = [];
    let handleEose = (i)=>{
      if (eosesReceived[i]) return; // do not act twice for the same relay
      eosesReceived[i] = true;
      if (eosesReceived.filter((a)=>a).length === groupedRequests.length) {
        params.oneose?.();
        handleEose = ()=>{};
      }
    };
    // batch all closes into a single
    const closesReceived = [];
    let handleClose = (i, reason)=>{
      if (closesReceived[i]) return; // do not act twice for the same relay
      handleEose(i);
      closesReceived[i] = reason;
      if (closesReceived.filter((a)=>a).length === groupedRequests.length) {
        params.onclose?.(closesReceived);
        handleClose = ()=>{};
      }
    };
    const localAlreadyHaveEventHandler = (id)=>{
      if (params.alreadyHaveEvent?.(id)) {
        return true;
      }
      const have = _knownIds.has(id);
      _knownIds.add(id);
      return have;
    };
    // open a subscription in all given relays
    const allOpened = Promise.all(groupedRequests.map(async ({ url, filters }, i)=>{
      if (this.allowConnectingToRelay?.(url, [
        'read',
        filters
      ]) === false) {
        handleClose(i, 'connection skipped by allowConnectingToRelay');
        return;
      }
      let relay;
      try {
        relay = await this.ensureRelay(url, {
          connectionTimeout: this.maxWaitForConnection < (params.maxWait || 0) ? Math.max(params.maxWait * 0.8, params.maxWait - 1000) : this.maxWaitForConnection,
          abort: params.abort
        });
      } catch (err) {
        this.onRelayConnectionFailure?.(url);
        handleClose(i, err?.message || String(err));
        return;
      }
      this.onRelayConnectionSuccess?.(url);
      let subscription = relay.subscribe(filters, {
        ...params,
        oneose: ()=>handleEose(i),
        onclose: (reason)=>{
          if (reason.startsWith('auth-required: ') && params.onauth) {
            relay.auth(params.onauth).then(()=>{
              relay.subscribe(filters, {
                ...params,
                oneose: ()=>handleEose(i),
                onclose: (reason)=>{
                  handleClose(i, reason); // the second time we won't try to auth anymore
                },
                alreadyHaveEvent: localAlreadyHaveEventHandler,
                eoseTimeout: params.maxWait,
                abort: params.abort
              });
            }).catch((err)=>{
              handleClose(i, `auth was required and attempted, but failed with: ${err}`);
            });
          } else {
            handleClose(i, reason);
          }
        },
        alreadyHaveEvent: localAlreadyHaveEventHandler,
        eoseTimeout: params.maxWait,
        abort: params.abort
      });
      subs.push(subscription);
    }));
    return {
      async close (reason) {
        await allOpened;
        subs.forEach((sub)=>{
          sub.close(reason);
        });
      }
    };
  }
  subscribeEose(relays, filter, params) {
    const subcloser = this.subscribe(relays, filter, {
      ...params,
      oneose () {
        subcloser.close('closed automatically on eose');
      }
    });
    return subcloser;
  }
  subscribeManyEose(relays, filter, params) {
    return this.subscribeEose(relays, filter, params);
  }
  async querySync(relays, filter, params) {
    return new Promise(async (resolve)=>{
      const events = [];
      this.subscribeEose(relays, filter, {
        ...params,
        onevent (event) {
          events.push(event);
        },
        onclose (_) {
          resolve(events);
        }
      });
    });
  }
  async get(relays, filter, params) {
    filter.limit = 1;
    const events = await this.querySync(relays, filter, params);
    events.sort((a, b)=>b.created_at - a.created_at);
    return events[0] || null;
  }
  publish(relays, event, params) {
    return relays.map(normalizeURL).map(async (url, i, arr)=>{
      if (arr.indexOf(url) !== i) {
        // duplicate
        return Promise.reject('duplicate url');
      }
      if (this.allowConnectingToRelay?.(url, [
        'write',
        event
      ]) === false) {
        return Promise.reject('connection skipped by allowConnectingToRelay');
      }
      let r;
      try {
        r = await this.ensureRelay(url, {
          connectionTimeout: this.maxWaitForConnection < (params?.maxWait || 0) ? Math.max(params.maxWait * 0.8, params.maxWait - 1000) : this.maxWaitForConnection,
          abort: params?.abort
        });
      } catch (err) {
        this.onRelayConnectionFailure?.(url);
        return String('connection failure: ' + String(err));
      }
      return r.publish(event).catch(async (err)=>{
        if (err instanceof Error && err.message.startsWith('auth-required: ') && params?.onauth) {
          await r.auth(params.onauth);
          return r.publish(event) // retry
          ;
        }
        throw err;
      }).then((reason)=>{
        if (this.trackRelays) {
          let set = this.seenOn.get(event.id);
          if (!set) {
            set = new Set();
            this.seenOn.set(event.id, set);
          }
          set.add(r);
        }
        return reason;
      });
    });
  }
  listConnectionStatus() {
    const map = new Map();
    this.relays.forEach((relay, url)=>map.set(url, relay.connected));
    return map;
  }
  destroy() {
    this.relays.forEach((conn)=>conn.close());
    this.relays = new Map();
  }
  pruneIdleRelays(idleThresholdMs = 10000) {
    const prunedUrls = [];
    // check each relay's idle status and prune if over threshold
    for (const [url, relay] of this.relays){
      if (relay.idleSince && Date.now() - relay.idleSince >= idleThresholdMs) {
        this.relays.delete(url);
        prunedUrls.push(url);
        relay.close();
      }
    }
    return prunedUrls;
  }
}
//# sourceMappingURL=abstract-pool.js.map